import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { DocPrepDocument } from '@et/typings';
import { TemplatesSearchListItemComponent } from '../templates-search-list-item/templates-search-list-item.component';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { PresentationalAtomsModule } from '../presentational-atoms.module';

const SCROLL_THRESHOLD = 50;

@Component({
  selector: 'et-atoms-templates-search-list',
  imports: [TemplatesSearchListItemComponent, PresentationalAtomsModule],
  templateUrl: './templates-search-list.component.html',
  styleUrl: './templates-search-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplatesSearchListComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private ngZone = inject(NgZone);

  @Input({ required: true }) templates!: DocPrepDocument[];
  @Input() loading = false;
  @Input() hasMore = false;
  @Output() templateSelected = new EventEmitter<DocPrepDocument>();
  @Output() loadMore = new EventEmitter<void>();

  @ViewChildren(TemplatesSearchListItemComponent)
  selectOptions!: QueryList<TemplatesSearchListItemComponent>;

  private keyManager!: ActiveDescendantKeyManager<TemplatesSearchListItemComponent>;
  private scrollListener: (() => void) | null = null;

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    this.handleKeyDown(event);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.keyManager && changes['templates']) {
      // Scroll to the first item if the list has changed
      const prevIds: string[] = changes['templates'].previousValue?.map(
        (t: DocPrepDocument) => t.id,
      );
      const newIds: string[] = changes['templates'].currentValue?.map(
        (t: DocPrepDocument) => t.id,
      );
      if (prevIds.every((id) => newIds.includes(id))) return;
      Promise.resolve().then(() => {
        this.keyManager?.setActiveItem(0);
        this.cdr.markForCheck();
      });
    }
  }

  ngAfterViewInit(): void {
    this.initKeyManager();
    this.setupScrollListener();
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
  }

  /**
   * Emits the selected template.
   *
   * @param {DocPrepDocument} template - The selected template.
   */
  onSelect(template: DocPrepDocument) {
    this.templateSelected.emit(template);
  }

  /**
   * Handles keyboard navigation within the list.
   *
   * This method listens for keyboard events and navigates through the list items using the arrow keys.
   * When the Enter key is pressed, it selects the currently active item.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  private handleKeyDown(event: KeyboardEvent) {
    const key = event.key;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      this.keyManager.onKeydown(event);
    } else if (key === 'Enter') {
      const template = this.keyManager.activeItem?.template;
      if (template) {
        this.onSelect(template);
        event.preventDefault();
      }
    }
  }

  /**
   * Initializes the key manager for keyboard navigation.
   *
   * This method sets up the `ActiveDescendantKeyManager` to manage keyboard navigation within the list.
   * It also sets the first item as the active item and triggers change detection.
   */
  private initKeyManager() {
    this.keyManager =
      new ActiveDescendantKeyManager<TemplatesSearchListItemComponent>(
        this.selectOptions,
      )
        .withTypeAhead()
        .withVerticalOrientation()
        .withHorizontalOrientation('ltr')
        .withHomeAndEnd()
        .withWrap();

    this.keyManager.setActiveItem(0);
    this.cdr.markForCheck();
  }

  /**
   * Sets up a scroll event listener on the host element.
   *
   * The listener is registered outside Angular's zone to avoid triggering
   * unnecessary change detection cycles on every scroll event.
   */
  private setupScrollListener(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    this.ngZone.runOutsideAngular(() => {
      this.scrollListener = () => this.onScroll(el);
      el.addEventListener('scroll', this.scrollListener, { passive: true });
    });
  }

  /**
   * Removes the scroll event listener from the host element.
   *
   * This method is called during component destruction to prevent memory leaks.
   */
  private removeScrollListener(): void {
    if (this.scrollListener) {
      const el = this.elementRef.nativeElement as HTMLElement;
      el.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }

  /**
   * Handles scroll events and triggers infinite scroll loading.
   *
   * When the user scrolls near the bottom of the list (within {@link SCROLL_THRESHOLD} pixels),
   * emits the `loadMore` event to request additional items.
   *
   * @param el - The scrollable host element.
   */
  private onScroll(el: HTMLElement): void {
    if (this.loading || !this.hasMore) return;
    const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (scrollBottom <= SCROLL_THRESHOLD) {
      this.ngZone.run(() => this.loadMore.emit());
    }
  }
}
